import { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, Mail, Phone, Lock, User, MapPin, Upload, Info, Camera, AlertCircle, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useContext } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { v4 as uuidv4 } from 'uuid';

const TERMS_TEXT = `TERMS & CONDITIONS
mybook.rent — India's First Peer-to-Peer Book Rental Platform
Last Updated: March 2026

Platform: mybook.rent
Legal Entity: BOOKONRENT
Registered Address: 312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087
Contact: support@mybook.rent | +91 96527 73409
Governing Law: Laws of India | Jurisdiction: Bengaluru, Karnataka

Thank you for visiting mybook.rent. For the purpose of these Terms and Conditions ("Terms"), the terms "we", "us", and "our" refer to mybook.rent, operated by BOOKONRENT. The terms "you", "your", "user", "visitor", "borrower", or "lender" refer to any natural or legal person visiting or using our Platform.

By accessing or using the mybook.rent Platform — whether as a borrower, a lender, or a general visitor — you agree to be bound by these Terms. If you do not agree with any part of these Terms, please do not use the Platform.

mybook.rent is a peer-to-peer book rental marketplace. We connect individual book lenders and borrowers. The books listed on our platform belong to individual community members — not to mybook.rent.

1. Platform Eligibility
By using our Platform, you confirm that:
• You are at least 18 years of age, or have the consent of a parent or legal guardian.
• You are legally capable of entering into a binding agreement under applicable Indian law.
• You will use the Platform only for lawful purposes and in accordance with these Terms.
• The information you provide is accurate, current, and complete.

2. The Platform — How mybook.rent Works
mybook.rent is a technology-driven, peer-to-peer book rental marketplace that enables:
• Lenders — community members who list their personal book collections for rent.
• Borrowers — readers seeking affordable access to books — to discover, book, and collect books from lenders in their neighbourhood.

mybook.rent is the facilitating platform — we are not the owner, seller, or custodian of any book listed. The rental relationship is between the lender and the borrower.

3. User Accounts
• You are responsible for maintaining the confidentiality of your account credentials.
• You must notify us immediately at support@mybook.rent if you suspect any unauthorised access.
• You may not create more than one account per individual.
• mybook.rent reserves the right to refuse registration, suspend, or permanently terminate any account at its sole discretion.

4. Lender Obligations
If you list books on mybook.rent as a lender, you agree to:
• Be the rightful owner of all books you list, or have the legal right to lend them.
• Provide accurate listing information — title, author, edition, condition, and rental price.
• Make the book available for pickup at the agreed location and time.
• Conduct a fair and honest condition check at both pickup and return.
• Acknowledge receipt of returned books within 24 hours.
• Not cancel confirmed bookings without good reason.

5. Borrower Obligations
If you borrow books, you agree to:
• Collect the booked book from the agreed location at the agreed time.
• Treat all borrowed books with care and return them in the same condition.
• Return the book by the agreed return date. Late returns attract late fees.
• Be responsible for the full replacement cost if a book is damaged.
• Be liable for the replacement cost plus a penalty fee if a book is lost.
• Not reproduce, photocopy, scan, or digitise any borrowed book.

6. Book Pickup & Self-Collection
mybook.rent currently operates on a self-pickup model:
• All book pickups and returns are made directly between borrower and lender.
• mybook.rent does not provide delivery or logistics services at this stage.
• Borrowers must respect the lender's pickup preferences.

7. Rental Fees & Payments
• Rental fees are set by individual lenders.
• All payments are processed through secure third-party payment gateways.
• All prices are in Indian Rupees (₹) inclusive of applicable taxes.
• A platform fee of 5% applies to each rental, plus applicable GST.

8. Cancellations & Refunds
• Cancellations until pickup time — full refund.
• After pickup time — cancellation is closed and there is no refund.
• Early returns within 24 hours of pickup — 50% refund.
• Damaged or lost books — borrower is liable for replacement cost.

9. Intellectual Property
All content on the Platform is the property of BOOKONRENT or its licensed content providers. You are granted a limited, non-exclusive, non-transferable licence for personal, non-commercial use only.

10. User Conduct
You agree not to:
• Upload unlawful, harmful, or objectionable content.
• Use the Platform for fraudulent or deceptive purposes.
• Attempt to bypass the platform's payment systems.
• Interfere with Platform servers or security systems.
• Use automated tools or scrapers.
• Impersonate any person or entity.

11. Platform Availability & Changes
mybook.rent strives to maintain availability but does not guarantee uninterrupted access. We reserve the right to modify any feature at any time.

12. Limitation of Liability
To the maximum extent permitted by law, mybook.rent shall not be liable for any damages arising from use of the Platform. Our total liability shall not exceed the amount paid by you in the 3 months preceding the claim.

13. Indemnification
You agree to indemnify and hold harmless mybook.rent, BOOKONRENT, its officers, directors, and agents from all claims arising from your use of the Platform.

14. Privacy & Data Protection
Your use is governed by our Privacy Policy, in accordance with the DPDP Act, 2023.

15. Termination
mybook.rent may suspend or terminate your access at any time. You may terminate by contacting support@mybook.rent. Outstanding obligations must be settled prior to closure.

16. Changes to These Terms
mybook.rent reserves the right to modify these Terms at any time. Continued use constitutes acceptance.

17. Governing Law & Jurisdiction
These Terms are governed by the laws of India. Disputes are subject to the exclusive jurisdiction of the courts of Bengaluru, Karnataka.

18. Contact Us
Email: support@mybook.rent
WhatsApp / Phone: +91 86607 85749
Address: 312, Incor Opulence, Gunjur Palya Road, Bengaluru, Karnataka – 560087

--- ADDENDUM ---

Clause 19: Security Deposit
The Security Deposit is a fully refundable wallet balance. You must hold at least 60% of a book's MRP in your deposit wallet before renting. Refunded upon account closure in good standing within 7-10 business days.

Clause 20: Rental Fees
Rental fees are set by lenders. A 5% platform fee and applicable GST are added at checkout.

Clause 21: Lost or Unreturned Premium & High-Value Books
Borrower is liable for the full replacement cost. Security deposit is deducted immediately. Shortfall is an outstanding debt. Persistent non-payment may result in permanent ban and legal action.

Clause 22: Lender Acknowledgement for Listed & Premium Books
Lenders voluntarily make books available under the access tier framework. mybook.rent's liability is limited to facilitating the Security Deposit process. Rare or irreplaceable books are advised not to be listed.`;

const RegisterPage = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTarget = searchParams.get('redirect');
  const afterRegister = redirectTarget && redirectTarget.startsWith('/') && !redirectTarget.startsWith('//')
    ? redirectTarget
    : '/';
  const profileImageRef = useRef(null);
  const qrCodeImageRef = useRef(null);
  const termsRef = useRef(null);
  
  const [userType, setUserType] = useState('customer');
  const [formData, setFormData] = useState({
    profile_image: null,
    profile_image_preview: '',
    pan_name: '',
    house_flat_no: '',
    street: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    email: '',
    password: '',
    confirm_password: '',
    upi_address: '',
    gpay_phonepe_number: '',
    qr_code_image: null,
    qr_code_preview: '',
    transaction_type: 'rent',
    media_type: 'print',
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [hasScrolledTerms, setHasScrolledTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const emailDomains = ['gmail.com', 'outlook.com', 'yahoo.com', 'hotmail.com', 'icloud.com'];

  const handleTermsScroll = useCallback((e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (atBottom) {
      setHasScrolledTerms(true);
    }
  }, []);

  const validateField = (name, value) => {
    let error = '';
    
    switch(name) {
      case 'pan_name':
        if (!value) {
          error = 'Name (as per PAN) is required';
        } else if (!/^[A-Za-z\s]+$/.test(value)) {
          error = 'Name must contain only alphabets';
        } else if (value.length < 4 || value.length > 30) {
          error = 'Name must be 4-30 characters';
        }
        break;
        
      case 'pincode':
        if (value && !/^\d{6}$/.test(value)) {
          error = 'PIN Code must be exactly 6 digits';
        }
        break;
        
      case 'phone':
        if (!value) {
          error = 'Phone number is required';
        } else if (!/^\d{10}$/.test(value)) {
          error = 'Phone must be exactly 10 digits';
        }
        break;
        
      case 'email':
        if (value) {
          if (value.length < 5) {
            error = 'Email must be at least 5 characters';
          } else if (!value.includes('@')) {
            error = 'Email must contain @';
          } else if (!value.match(/\.[a-z]{2,}$/i)) {
            error = 'Email must have a valid domain (e.g., .com)';
          }
        }
        break;
        
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        }
        break;
        
      case 'confirm_password':
        if (!value) {
          error = 'Please confirm your password';
        } else if (value !== formData.password) {
          error = 'Passwords do not match';
        }
        break;
        
      case 'upi_address':
        if (value && !value.match(/^[\w.-]+@[\w.-]+$/)) {
          error = 'Invalid UPI format (e.g., name@bank)';
        }
        break;
        
      case 'gpay_phonepe_number':
        if (value && !/^\d{10}$/.test(value)) {
          error = 'Must be exactly 10 digits';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = value;
    
    if (name === 'pan_name') {
      newValue = value.replace(/[^A-Za-z\s]/g, '');
    } else if (name === 'pincode' || name === 'phone' || name === 'gpay_phonepe_number') {
      newValue = value.replace(/\D/g, '');
    }
    
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : newValue 
    });
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
    
    if (name === 'email' && value.includes('@') && !value.includes('.')) {
      const username = value.split('@')[0];
      const suggestions = emailDomains.map(domain => `${username}@${domain}`);
      setEmailSuggestions(suggestions);
    } else {
      setEmailSuggestions([]);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors({ ...errors, [name]: error });
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, profile_image: 'Only PNG/JPG/JPEG formats allowed' });
        return;
      }
      
      if (file.size > 2 * 1024 * 1024) {
        setErrors({ ...errors, profile_image: 'Image size must be less than 2MB' });
        toast.error('Profile image must be less than 2MB');
        return;
      }
      
      setFormData({ 
        ...formData, 
        profile_image: file,
        profile_image_preview: URL.createObjectURL(file)
      });
      setErrors({ ...errors, profile_image: '' });
    }
  };

  const handleQRCodeImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['image/png', 'image/jpg', 'image/jpeg'];
      if (!validTypes.includes(file.type)) {
        setErrors({ ...errors, qr_code_image: 'Only PNG/JPG/JPEG formats allowed' });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, qr_code_image: 'Image size must be less than 5MB' });
        return;
      }
      
      setFormData({ 
        ...formData, 
        qr_code_image: file,
        qr_code_preview: URL.createObjectURL(file)
      });
      setErrors({ ...errors, qr_code_image: '' });
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
          );
          const data = await response.json();
          if (data.address) {
            const addr = data.address;
            setFormData((prev) => ({
              ...prev,
              house_flat_no: addr.house_number || prev.house_flat_no,
              street: addr.road || addr.neighbourhood || prev.street,
              city: addr.city || addr.town || addr.village || prev.city,
              state: addr.state || prev.state,
              pincode: addr.postcode || prev.pincode,
            }));
            toast.success('Address auto-filled from your location!');
          } else {
            toast.success('Location detected! Please fill in your address details.');
          }
        } catch (e) {
          toast.error('Could not auto-fill address. Please enter manually.');
        }
      },
      () => {
        toast.error('Location access denied');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const validateAllFields = () => {
    const newErrors = {};
    
    newErrors.pan_name = validateField('pan_name', formData.pan_name);
    newErrors.phone = validateField('phone', formData.phone);
    newErrors.password = validateField('password', formData.password);
    newErrors.confirm_password = validateField('confirm_password', formData.confirm_password);
    
    if (formData.email) {
      const emailError = validateField('email', formData.email);
      if (emailError) newErrors.email = emailError;
    }
    if (formData.pincode) {
      const pincodeError = validateField('pincode', formData.pincode);
      if (pincodeError) newErrors.pincode = pincodeError;
    }
    if (formData.upi_address) {
      const upiError = validateField('upi_address', formData.upi_address);
      if (upiError) newErrors.upi_address = upiError;
    }
    if (formData.gpay_phonepe_number) {
      const gpayError = validateField('gpay_phonepe_number', formData.gpay_phonepe_number);
      if (gpayError) newErrors.gpay_phonepe_number = gpayError;
    }

    if (!hasScrolledTerms) {
      newErrors.terms = 'Please scroll through the complete Terms & Conditions';
    }
    
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getProfileCompletionPercentage = () => {
    const fields = ['pan_name', 'phone', 'password', 'email', 'house_flat_no', 'street', 'city', 'pincode', 'profile_image'];
    const filledFields = fields.filter(field => {
      if (field === 'profile_image') return formData.profile_image !== null;
      return formData[field] && formData[field].trim() !== '';
    });
    return Math.round((filledFields.length / fields.length) * 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateAllFields()) {
      toast.error('Please fix all errors before submitting');
      return;
    }
    
    const userId = uuidv4();
    
    const registrationData = new FormData();
    registrationData.append('role', userType);
    registrationData.append('user_id', userId);
    registrationData.append('name', formData.pan_name);
    registrationData.append('pan_name', formData.pan_name);
    registrationData.append('phone', formData.phone);
    registrationData.append('password', formData.password);
    if (formData.email) registrationData.append('email', formData.email);
    if (formData.house_flat_no || formData.street) {
      registrationData.append('address', `${formData.house_flat_no || ''}, ${formData.street || ''}`.trim());
    }
    if (formData.city) registrationData.append('city', formData.city);
    if (formData.state) registrationData.append('state', formData.state);
    if (formData.pincode) registrationData.append('pincode', formData.pincode);
    if (formData.profile_image) registrationData.append('profile_image', formData.profile_image);
    
    if (userType === 'admin') {
      if (formData.upi_address) registrationData.append('upi_address', formData.upi_address);
      if (formData.gpay_phonepe_number) registrationData.append('gpay_phonepe_number', formData.gpay_phonepe_number);
      registrationData.append('transaction_type', formData.transaction_type);
      registrationData.append('media_type', formData.media_type);
      if (formData.qr_code_image) registrationData.append('qr_code_image', formData.qr_code_image);
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registrationData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      login(response.data.token, response.data.user);
      toast.success('Registration successful! Your User ID: ' + userId);
      navigate(afterRegister);
    } catch (error) {
      console.error('Registration error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  const profileCompletion = getProfileCompletionPercentage();

  return (
    <div className="min-h-screen flex items-center justify-center p-4 py-12" style={{background: 'var(--background)'}}>
      <div className="w-full max-w-3xl">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-3 mb-4 cursor-pointer" onClick={() => navigate('/')}>
            <BookOpen className="h-12 w-12" style={{color: 'var(--primary)'}} />
            <h1 className="text-4xl font-bold" style={{color: 'var(--primary)'}}>
              RentMyBook
            </h1>
          </div>
          <p className="text-gray-600">Create your account and start reading</p>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8">
          {/* Profile Completion Reminder */}
          <div className="mb-6 p-4 rounded-xl" style={{backgroundColor: profileCompletion < 100 ? '#FEF3C7' : '#D1FAE5'}}>
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5" style={{color: profileCompletion < 100 ? '#D97706' : '#059669'}} />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{color: profileCompletion < 100 ? '#92400E' : '#065F46'}}>
                  Profile Completion: {profileCompletion}%
                </p>
                <p className="text-xs" style={{color: profileCompletion < 100 ? '#B45309' : '#047857'}}>
                  {profileCompletion < 100 
                    ? 'Complete your profile to unlock all features like renting and listing books!' 
                    : 'Great! Your profile is complete.'}
                </p>
              </div>
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
              <div 
                className="h-2 rounded-full transition-all duration-300" 
                style={{
                  width: `${profileCompletion}%`,
                  backgroundColor: profileCompletion < 100 ? '#F59E0B' : '#10B981'
                }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label className="text-base font-semibold mb-3 block">I want to create an account as:</Label>
              <RadioGroup value={userType} onValueChange={setUserType} className="flex space-x-4">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="customer" id="customer" data-testid="customer-radio" />
                  <Label htmlFor="customer" className="cursor-pointer">Borrower</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="admin" id="admin" data-testid="admin-radio" />
                  <Label htmlFor="admin" className="cursor-pointer">Prosumer (Book Owner)</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg" style={{color: 'var(--primary)'}}>Personal Information</h3>
              
              {/* Profile Picture Upload */}
              <div>
                <Label>Profile Picture</Label>
                <div className="mt-2 flex items-center space-x-4">
                  <div 
                    className="w-24 h-24 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                    style={{borderColor: 'var(--primary)'}}
                    onClick={() => profileImageRef.current?.click()}
                  >
                    {formData.profile_image_preview ? (
                      <img src={formData.profile_image_preview} alt="Profile" className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <Camera className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={profileImageRef}
                      onChange={handleProfileImageChange}
                      accept="image/png,image/jpg,image/jpeg"
                      className="hidden"
                      data-testid="profile-image-input"
                    />
                    <Button type="button" variant="outline" onClick={() => profileImageRef.current?.click()}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Photo
                    </Button>
                    <p className="text-xs text-gray-500 mt-1">PNG/JPG/JPEG (Max 2MB)</p>
                  </div>
                </div>
                {errors.profile_image && <p className="text-red-500 text-sm mt-1">{errors.profile_image}</p>}
              </div>

              {/* Name (as per PAN) */}
              <div>
                <Label>Name (as per PAN) *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    name="pan_name"
                    value={formData.pan_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your name (4-30 characters)"
                    required
                    maxLength={30}
                    className="pl-10"
                    data-testid="pan-name-input"
                  />
                </div>
                {errors.pan_name && <p className="text-red-500 text-sm mt-1">{errors.pan_name}</p>}
              </div>

              {/* Address Section - Optional for all users */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Address (Optional)</Label>
                  <Button type="button" variant="link" size="sm" onClick={detectLocation} className="text-xs">
                    <MapPin className="h-3 w-3 mr-1" />
                    Detect Location
                  </Button>
                </div>
                <p className="text-xs text-amber-600 mb-2">Address is optional during sign-up. It becomes mandatory before you can add or rent a book.</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        name="house_flat_no"
                        value={formData.house_flat_no}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="House/Flat No."
                        data-testid="house-flat-input"
                      />
                      {errors.house_flat_no && <p className="text-red-500 text-sm mt-1">{errors.house_flat_no}</p>}
                    </div>
                    <div>
                      <Input
                        name="street"
                        value={formData.street}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Street"
                        data-testid="street-input"
                      />
                      {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Input
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="City"
                        data-testid="city-input"
                      />
                      {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                    </div>
                    <div>
                      <Input
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="State"
                        data-testid="state-input"
                      />
                    </div>
                    <div>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <Input
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder="PIN Code"
                          maxLength={6}
                          className="pl-10"
                          data-testid="pincode-input"
                        />
                      </div>
                      {errors.pincode && <p className="text-red-500 text-sm mt-1">{errors.pincode}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Phone Number - Mandatory (MBR-080) */}
              <div>
                <Label>Phone Number *</Label>
                <p className="text-xs text-gray-500 mb-1">Required for pickup codes and account login.</p>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="10-digit phone number"
                    maxLength={10}
                    required
                    className="pl-10"
                    data-testid="phone-input"
                  />
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Email Address - Optional */}
              <div>
                <Label>Email Address (Optional)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter your email (optional)"
                    className="pl-10"
                    data-testid="email-input"
                  />
                </div>
                {emailSuggestions.length > 0 && (
                  <div className="mt-1 border rounded-md bg-white shadow-sm">
                    {emailSuggestions.map((suggestion, idx) => (
                      <div
                        key={idx}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                        onClick={() => {
                          setFormData({ ...formData, email: suggestion });
                          setEmailSuggestions([]);
                        }}
                      >
                        {suggestion}
                      </div>
                    ))}
                  </div>
                )}
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Password */}
              <div>
                <Label>Password *</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="At least 6 characters"
                    required
                    className="pl-10 pr-10"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              {/* Confirm Password */}
              <div>
                <Label>Confirm Password *</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <Input
                    name="confirm_password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirm_password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Re-enter your password"
                    required
                    className="pl-10 pr-10"
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirm_password && <p className="text-red-500 text-sm mt-1">{errors.confirm_password}</p>}
              </div>
            </div>

            {/* Admin Section - Bank Details Optional */}
            {userType === 'admin' && (
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-lg" style={{color: 'var(--primary)'}}>Prosumer Details (Optional)</h3>
                <p className="text-xs text-amber-600 -mt-2">Bank/UPI details are optional during sign-up. They become mandatory before you can add a book.</p>
                
                {/* UPI Address */}
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Label>UPI Address</Label>
                    <div className="group relative">
                      <Info className="h-4 w-4 cursor-help" style={{color: 'var(--accent)'}} />
                      <div className="hidden group-hover:block absolute left-0 top-6 bg-gray-900 text-white text-xs rounded px-2 py-1 w-48 z-10">
                        UPI details are required to process rental payments from customers
                      </div>
                    </div>
                  </div>
                  <Input
                    name="upi_address"
                    value={formData.upi_address}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="yourname@bank (e.g., name@paytm)"
                    data-testid="upi-address-input"
                  />
                  {errors.upi_address && <p className="text-red-500 text-sm mt-1">{errors.upi_address}</p>}
                </div>

                {/* GPay/PhonePe Number */}
                <div>
                  <Label>GPay / PhonePe Mobile Number</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      name="gpay_phonepe_number"
                      type="tel"
                      value={formData.gpay_phonepe_number}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="10-digit number"
                      maxLength={10}
                      className="pl-10"
                      data-testid="gpay-phonepe-input"
                    />
                  </div>
                  {errors.gpay_phonepe_number && <p className="text-red-500 text-sm mt-1">{errors.gpay_phonepe_number}</p>}
                </div>

                {/* QR Code Upload */}
                <div>
                  <Label>UPI QR Code (Optional)</Label>
                  <div className="mt-2 flex items-start space-x-4">
                    <div 
                      className="w-32 h-32 border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                      style={{borderColor: 'var(--primary)'}}
                      onClick={() => qrCodeImageRef.current?.click()}
                    >
                      {formData.qr_code_preview ? (
                        <img src={formData.qr_code_preview} alt="QR Code" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <div className="text-center">
                          <Upload className="h-8 w-8 text-gray-400 mx-auto mb-1" />
                          <p className="text-xs text-gray-500">Upload QR</p>
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        ref={qrCodeImageRef}
                        onChange={handleQRCodeImageChange}
                        accept="image/png,image/jpg,image/jpeg"
                        className="hidden"
                        data-testid="qr-code-input"
                      />
                      <Button type="button" variant="outline" onClick={() => qrCodeImageRef.current?.click()}>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload QR Code
                      </Button>
                      <p className="text-xs text-gray-500 mt-1">PNG/JPG/JPEG (Max 5MB)</p>
                    </div>
                  </div>
                  {errors.qr_code_image && <p className="text-red-500 text-sm mt-1">{errors.qr_code_image}</p>}
                </div>

                {/* Transaction Type */}
                <div>
                  <Label className="mb-2 block">Transaction Type *</Label>
                  <RadioGroup 
                    name="transaction_type"
                    value={formData.transaction_type} 
                    onValueChange={(value) => setFormData({...formData, transaction_type: value})}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" style={{borderColor: formData.transaction_type === 'rent' ? 'var(--primary)' : '#e5e7eb'}}>
                      <RadioGroupItem value="rent" id="rent" data-testid="rent-radio" />
                      <Label htmlFor="rent" className="cursor-pointer">Rent</Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" style={{borderColor: formData.transaction_type === 'resale' ? 'var(--primary)' : '#e5e7eb'}}>
                      <RadioGroupItem value="resale" id="resale" data-testid="resale-radio" />
                      <Label htmlFor="resale" className="cursor-pointer">Resale</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Media Type */}
                <div>
                  <Label className="mb-2 block">Media Type *</Label>
                  <RadioGroup 
                    name="media_type"
                    value={formData.media_type} 
                    onValueChange={(value) => setFormData({...formData, media_type: value})}
                    className="flex space-x-4"
                  >
                    <div className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" style={{borderColor: formData.media_type === 'ebook' ? 'var(--primary)' : '#e5e7eb'}}>
                      <RadioGroupItem value="ebook" id="ebook" data-testid="ebook-radio" />
                      <Label htmlFor="ebook" className="cursor-pointer">E-book</Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50" style={{borderColor: formData.media_type === 'print' ? 'var(--primary)' : '#e5e7eb'}}>
                      <RadioGroupItem value="print" id="print" data-testid="print-radio" />
                      <Label htmlFor="print" className="cursor-pointer">Print</Label>
                    </div>
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* Terms & Conditions - Scrollable Text */}
            <div className="pt-4 border-t">
              <h3 className="font-semibold text-lg mb-2" style={{color: 'var(--primary)'}}>Terms &amp; Conditions</h3>
              <p className="text-xs text-gray-500 mb-2">Please read and scroll to the end of the Terms &amp; Conditions to enable the Create Account button.</p>
              <div 
                ref={termsRef}
                onScroll={handleTermsScroll}
                className="border rounded-lg p-4 bg-gray-50 overflow-y-auto text-xs text-gray-700 leading-relaxed whitespace-pre-wrap"
                style={{maxHeight: '200px'}}
                data-testid="terms-scroll-container"
              >
                {TERMS_TEXT}
              </div>
              {!hasScrolledTerms && (
                <div className="flex items-center justify-center mt-2 text-xs text-amber-600">
                  <ChevronDown className="h-4 w-4 mr-1 animate-bounce" />
                  Scroll down to read the complete Terms &amp; Conditions
                </div>
              )}
              {hasScrolledTerms && (
                <p className="text-xs text-green-600 mt-2 font-medium">You have read the Terms &amp; Conditions</p>
              )}
              {errors.terms && <p className="text-red-500 text-sm mt-1">{errors.terms}</p>}
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1 h-12"
                onClick={handleCancel}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 h-12 hover:opacity-90"
                style={{
                  backgroundColor: hasScrolledTerms ? 'var(--primary)' : '#d1d5db',
                  color: hasScrolledTerms ? 'white' : '#9ca3af',
                  cursor: hasScrolledTerms ? 'pointer' : 'not-allowed'
                }}
                disabled={loading || !hasScrolledTerms}
                data-testid="register-submit-button"
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate(redirectTarget ? `/login?redirect=${encodeURIComponent(redirectTarget)}` : '/login')}
                className="font-semibold hover:underline"
                style={{color: 'var(--accent)'}}
                data-testid="go-to-login-button"
              >
                Login
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
